import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Activity, Bot, Cpu, KeyRound, Loader2, RefreshCw, User } from 'lucide-react';
import { checkoutApi, type LiveCheckout } from '../../services/api';
import { liveCheckouts } from '../../services/liveCheckouts';
import { waysApi, WaysError, type WaysPresenceRow } from '../../services/waysApi';
import { WaysSignInRequired } from '../../services/waysAuth';
import { relTime, toDate } from './waysTime';

const POLL_MS = 12_000;      // the PMO change feed, and Ways presence
const RESYNC_MS = 120_000;   // a full re-read with last-worked, in case a change was missed

type Kind = LiveCheckout['holderKind'];
const KINDS: { id: 'all' | Kind; label: string }[] = [
  { id: 'all', label: 'All' }, { id: 'runner', label: 'Runners' }, { id: 'agent', label: 'Agents' },
  { id: 'person', label: 'People' },
];

function KindIcon({ kind }: { kind: Kind }) {
  const cls = 'w-3.5 h-3.5 shrink-0';
  if (kind === 'runner') return <Bot className={clsx(cls, 'text-violet-500')} aria-label="runner" />;
  if (kind === 'agent') return <Cpu className={clsx(cls, 'text-sky-500')} aria-label="agent session" />;
  if (kind === 'token') return <KeyRound className={clsx(cls, 'text-gray-400')} aria-label="integration token" />;
  return <User className={clsx(cls, 'text-gray-500')} aria-label="person" />;
}

function holderText(r: LiveCheckout, p?: WaysPresenceRow): string {
  const h = r.checkout.holder || {};
  if (r.holderKind === 'runner') return `runner ${r.runnerSession || h.label || ''}`.trim();
  if (r.holderKind === 'agent') {
    const s = r.activeSessions.find((x) => x.agent);
    return s?.label || p?.holder.label || h.label || h.email || 'agent session';
  }
  return h.label || h.email || p?.holder.label || h.id || '—';
}

/**
 * Active now (hs-pmo/ep19#43; Zane 2026-10-24: "when i am looking at ways in pmo the runners and
 * agents dont seem to align with what is going on"). Every live PMO checkout -- a person, an agent
 * session or a runner holding an item -- merged with Ways presence (who is ACTIVELY working it).
 * PMO is the record: rows come from GET /workitems/checkouts/live and follow the checkout change
 * feed from its cursor, so they appear and disappear as work is taken and released, without a
 * reload. Presence only adds the "working now" mark; without it the view still shows every hold.
 */
export default function WaysActiveNow({ projectName }: { projectName: (id?: string | null) => string }) {
  const [rows, setRows] = useState<Record<string, LiveCheckout> | null>(null);
  const [worked, setWorked] = useState<Record<string, { lastWorked?: string | null; idle?: boolean }>>({});
  const [presence, setPresence] = useState<Record<string, WaysPresenceRow> | null>(null);
  const [presenceNote, setPresenceNote] = useState('');
  const [error, setError] = useState('');
  const [at, setAt] = useState<string>('');
  const [kind, setKind] = useState<'all' | Kind>('all');
  const [fresh, setFresh] = useState<Record<string, number>>({});
  const [now, setNow] = useState(() => Date.now());
  const cursor = useRef('');
  const known = useRef<Set<string>>(new Set());
  const busy = useRef(false);

  /** Rows that were not there before flash briefly, so a new hold is noticed. */
  const take = useCallback((items: LiveCheckout[]) => {
    const next: Record<string, LiveCheckout> = {};
    const added: Record<string, number> = {};
    for (const r of items) {
      next[r.id] = r;
      if (known.current.size && !known.current.has(r.id)) added[r.id] = Date.now();
    }
    known.current = new Set(Object.keys(next));
    setRows(next);
    if (Object.keys(added).length) setFresh((f) => ({ ...f, ...added }));
  }, []);

  const loadWorked = useCallback(async () => {
    try {
      const r = await liveCheckouts(POLL_MS, true);
      // only the last-worked column: the rows follow the feed, and this read may be older than a release
      setWorked(Object.fromEntries(r.items.map((i) => [i.id, { lastWorked: i.lastWorked, idle: i.idle }])));
    } catch { /* last-worked is extra: the rows are already shown */ }
  }, []);

  /** `maxAgeMs` > 0 takes the shared read when it is that fresh (opening the view); a change on the
   *  feed, the resync and Refresh always go to PMO. An older cursor only replays a few events. */
  const loadAll = useCallback(async (maxAgeMs = 0) => {
    const r = await liveCheckouts(maxAgeMs);
    cursor.current = r.cursor;
    setAt(r.at);
    take(r.items);
    setError('');
  }, [take]);

  const loadPresence = useCallback(async () => {
    try {
      const p = await waysApi.presence();
      setPresence(Object.fromEntries((p.items || []).map((i) => [i.item, i])));
      setPresenceNote('');
    } catch (e) {
      setPresence(null);
      setPresenceNote(e instanceof WaysSignInRequired ? 'Sign in to Ways to see who is working each item right now.'
        : e instanceof WaysError && e.code === 'not_available_yet' ? 'Ways presence is not available on this server yet.'
          : `Ways presence could not be read (${(e as Error).message || 'unknown error'}).`);
    }
  }, []);

  /** Follow the change feed from the cursor: a release drops its row at once; a take or renewal
   *  re-reads the list (it carries the holder, its sessions and the lease). */
  const tick = useCallback(async () => {
    if (busy.current || document.hidden) return;
    busy.current = true;
    try {
      if (!cursor.current) {
        await loadAll();
      } else {
        const ch = await checkoutApi.changes(cursor.current);
        if (ch.cursor) cursor.current = ch.cursor;
        const last: Record<string, string> = {};
        for (const e of ch.events) if (e.item) last[e.item] = e.event;
        const gone = Object.entries(last).filter(([, ev]) => ev === 'released').map(([id]) => id);
        const reread = Object.values(last).some((ev) => ev !== 'released') || ch.more;
        if (gone.length) {
          setRows((cur) => {
            if (!cur) return cur;
            const next = { ...cur };
            for (const id of gone) delete next[id];
            known.current = new Set(Object.keys(next));
            return next;
          });
        }
        if (reread) await loadAll();
        else setAt(new Date().toISOString());
        setError('');
      }
    } catch (e) {
      setError((e as Error).message || 'Could not read checkouts');
    } finally {
      busy.current = false;
    }
    await loadPresence();
    setNow(Date.now());
  }, [loadAll, loadPresence]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { await loadAll(POLL_MS); } catch (e) { if (alive) setError((e as Error).message || 'Could not read checkouts'); }
      if (!alive) return;
      loadPresence();
      loadWorked();
    })();
    const t = window.setInterval(tick, POLL_MS);
    const r = window.setInterval(() => {
      if (document.hidden) return;
      loadAll().catch(() => undefined);
      loadWorked();
    }, RESYNC_MS);
    return () => { alive = false; window.clearInterval(t); window.clearInterval(r); };
  }, [loadAll, loadPresence, loadWorked, tick]);

  // the flash on a new row fades after a few seconds
  useEffect(() => {
    if (!Object.keys(fresh).length) return;
    const t = window.setTimeout(() => setFresh((f) => Object.fromEntries(
      Object.entries(f).filter(([, ts]) => Date.now() - ts < 6000))), 6000);
    return () => window.clearTimeout(t);
  }, [fresh]);

  // A lease that ran out is no checkout, even before the dispatcher clears it.
  const live = useMemo(() => Object.values(rows || {}).filter((r) => {
    const u = toDate(r.checkout.until);
    return !u || u.getTime() > now;
  }), [rows, now]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: live.length, runner: 0, agent: 0, person: 0, token: 0 };
    for (const r of live) c[r.holderKind] = (c[r.holderKind] || 0) + 1;
    return c;
  }, [live]);

  const workingNow = useMemo(() => live.filter((r) => presence?.[r.id]?.active).length, [live, presence]);

  /** Items Ways still shows as held that PMO has released: Ways mirrors every 15 s, so a few
   *  seconds of this is normal; it is counted, never shown as a hold. */
  const waysOnly = useMemo(() => (presence ? Object.keys(presence).filter((id) => !rows?.[id]).length : 0),
    [presence, rows]);

  const groups = useMemo(() => {
    const shown = live.filter((r) => kind === 'all' || r.holderKind === kind || (kind === 'person' && r.holderKind === 'token'));
    const by: Record<string, LiveCheckout[]> = {};
    for (const r of shown) (by[r.projectId || ''] ||= []).push(r);
    for (const list of Object.values(by)) {
      list.sort((a, b) => Number(!!presence?.[b.id]?.active) - Number(!!presence?.[a.id]?.active)
        || String(b.checkout.since || '').localeCompare(String(a.checkout.since || '')));
    }
    return Object.entries(by).sort(([a], [b]) => projectName(a).localeCompare(projectName(b)));
  }, [live, kind, presence, projectName]);

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Activity className="w-4 h-4 text-accent" /> Active now
          <span className="text-xs font-normal text-gray-500">
            {counts.all} checked out{presence ? ` · ${workingNow} being worked` : ''}
          </span>
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex rounded border border-gray-200 dark:border-gray-700 text-[11px] overflow-hidden">
            {KINDS.map((k) => (
              <button key={k.id} type="button" onClick={() => setKind(k.id)}
                className={clsx('px-2 py-0.5', kind === k.id ? 'bg-accent text-white' : 'text-gray-500 hover:text-accent')}>
                {k.label} {k.id === 'person' ? counts.person + counts.token : counts[k.id]}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => { loadAll().catch(() => undefined); loadPresence(); loadWorked(); }}
            title="Re-read now" className="p-1 text-gray-400 hover:text-accent">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <span className="text-[11px] text-gray-400">
            {at ? `as of ${new Date(at).toLocaleTimeString()} · live every ${POLL_MS / 1000}s` : ''}
          </span>
        </div>
      </div>
      {presenceNote && (
        <p className="mb-3 text-[11px] text-gray-500">
          {presenceNote} Showing PMO checkouts only: who holds what, not who is working it this minute.
        </p>
      )}
      {waysOnly > 0 && (
        <p className="mb-3 text-[11px] text-gray-400">
          Ways still lists {waysOnly} item{waysOnly === 1 ? '' : 's'} PMO has already checked in; it catches up within 15 s.
        </p>
      )}
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {rows === null ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : groups.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">Nothing is checked out right now.</p>
      ) : (
        <div className="space-y-4">
          {groups.map(([pid, list]) => (
            <section key={pid}>
              <h3 className="mb-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
                {projectName(pid) || 'No project'} <span className="font-normal text-gray-400">· {list.length}</span>
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="py-1.5 pr-3 font-medium">Held by</th>
                      <th className="py-1.5 pr-3 font-medium">Item</th>
                      <th className="py-1.5 pr-3 font-medium">Now</th>
                      <th className="py-1.5 pr-3 font-medium">Since</th>
                      <th className="py-1.5 pr-3 font-medium">Lease</th>
                      <th className="py-1.5 pr-3 font-medium">Last worked</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                    {list.map((r) => {
                      const p = presence?.[r.id];
                      const w = worked[r.id];
                      const h = r.checkout.holder || {};
                      return (
                        <tr key={r.id} className={clsx('transition-colors duration-1000', fresh[r.id] && 'bg-accent/10')}>
                          <td className="py-1.5 pr-3">
                            <span className="flex items-center gap-1.5" title={h.email || h.id || ''}>
                              <KindIcon kind={r.holderKind} />
                              <span className="truncate max-w-[16rem]">{holderText(r, p)}</span>
                              {h.actsFor && h.actsFor !== holderText(r, p) && (
                                <span className="text-gray-400">for {h.actsFor}</span>
                              )}
                            </span>
                          </td>
                          <td className="py-1.5 pr-3">
                            <Link to={`/item/${r.id}`} className="text-accent hover:underline" title={r.title}>
                              {r.key || r.displayId || r.id}
                            </Link>
                            <span className="ml-1.5 text-gray-500 truncate inline-block max-w-[22rem] align-bottom">{r.title}</span>
                          </td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">
                            {p?.active ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"
                                title={`marked active ${relTime(p.active.since, now)} by ${p.active.by}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> working
                              </span>
                            ) : w?.idle ? (
                              <span className="text-amber-600 dark:text-amber-400" title="No work recorded on it for 60 minutes: the idle sweep will check it in">idle</span>
                            ) : (
                              <span className="text-gray-400">held</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-3 whitespace-nowrap" title={r.checkout.since}>{relTime(r.checkout.since, now)}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap" title={r.checkout.until}>{relTime(r.checkout.until, now)}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap" title={w?.lastWorked || ''}>
                            {w ? relTime(w.lastWorked, now) : '…'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
